import React, { useMemo, useContext, useCallback } from 'react';
import { TableContext } from './context/TableContext';

const calculateMaxBid = (maxPrice, profitMargin, buyersPremium, exchangeRate, isSameCurrency) => {
  if (!maxPrice) return null;
  
  // Target price is max price minus profit margin
  const targetPrice = maxPrice * (1 - profitMargin / 100);
  
  // Max bid accounting for buyer's premium
  const maxBid = targetPrice / (1 + buyersPremium / 100);
  
  // If currencies are the same, no conversion needed
  if (isSameCurrency) {
    return Math.round(maxBid);
  }
  
  // Otherwise convert to house currency
  if (!exchangeRate) return null;
  return Math.round(maxBid / exchangeRate);
};

const TableFooter = ({ data, visibleColumns: rawVisibleColumns, columnWidths = {} }) => {
  const { isColumnPinned, getVisibleColumns, buyersPremium, houseCurrency, userCurrency, profitMargin, rate } = useContext(TableContext);
  
  // Use getVisibleColumns to get properly ordered columns with pinned ones first
  const visibleColumns = useMemo(() => 
    getVisibleColumns(rawVisibleColumns),
    [getVisibleColumns, rawVisibleColumns]
  );

  // Calculate pinned column offsets including checkbox column
  const getPinnedOffset = useCallback((columnField) => {
    let offset = 50; // Start with checkbox column width
    for (const col of visibleColumns) {
      if (col.field === columnField) {
        break;
      }
      if (isColumnPinned(col.field)) {
        offset += (columnWidths[col.field] || 150);
      }
    }
    return offset;
  }, [visibleColumns, isColumnPinned, columnWidths]);

  const totals = useMemo(() => {
    const priceColumns = [
      'purchase_price', 'shipping_cost', 'grading_cost', 'total_cost',
      'price_guide_value', 'list_price', 'sale_price', 'fees',
      'net_amount', 'profit', 'margin_percentage', 'total_value',
      'max_price', 'max_bid', 'winning_bid', 'total_purchases'
    ];
    
    // Calculate totals for all price columns
    const result = visibleColumns.reduce((acc, col) => {
      if (priceColumns.includes(col.field)) {
        if (col.field === 'margin_percentage') {
          // Calculate margin based on total profit and total cost
          const totalProfit = data.reduce((sum, row) => {
            const value = parseFloat(row.profit || 0);
            return sum + (isNaN(value) ? 0 : value);
          }, 0);
          const totalCost = data.reduce((sum, row) => {
            const value = parseFloat(row.total_cost || 0);
            return sum + (isNaN(value) ? 0 : value);
          }, 0);
          
          if (totalCost > 0) {
            acc[col.field] = (totalProfit / totalCost) * 100;
          }
        } else {
          let total;
          if (col.field === 'total_cost') {
            // Check if we're in auction planner context (has buyersPremium)
            if (buyersPremium) {
              total = data.reduce((sum, row) => {
                if (!['won', 'imported'].includes(row.status) || !row.winning_bid) {
                  return sum;
                }
                const value = row.winning_bid * (1 + buyersPremium / 100);
                return sum + (isNaN(value) ? 0 : value);
              }, 0);
            } else {
              // Regular inventory total cost calculation
              total = data.reduce((sum, row) => {
                const purchase = parseFloat(row.purchase_price) || 0;
                const shipping = parseFloat(row.shipping_cost) || 0;
                const grading = parseFloat(row.grading_cost) || 0;
                return sum + purchase + shipping + grading;
              }, 0);
            }
          } else if (col.field === 'max_bid') {
            total = data.reduce((sum, row) => {
              const maxBid = calculateMaxBid(
                row.max_price,
                profitMargin,
                buyersPremium,
                rate,
                houseCurrency === userCurrency
              );
              return sum + (maxBid || 0);
            }, 0);
          } else {
            total = parseFloat(data.reduce((sum, row) => {
              const value = parseFloat(row[col.field] || 0);
              return sum + (isNaN(value) ? 0 : value);
            }, 0).toFixed(2));
          }
          
          // Show totals even if they're 0 or negative for monetary columns
          acc[col.field] = total;
        }
      }
      return acc;
    }, {});

    return result;
  }, [data, visibleColumns]);

  return (
    <>
      {visibleColumns.map((column, index) => {
        const total = totals[column.field];
        const isPinned = isColumnPinned(column.field);
        return (
          <td 
            key={column.field}
            className={`
              px-3 py-2 text-xs text-gray-500 relative
              ${isPinned 
                ? "sticky z-[21] before:absolute before:inset-0 before:bg-indigo-50 before:-z-[1] shadow-[2px_0_5px_-2px_rgba(0,0,0,0.15)]"
                : "bg-indigo-50"
              }
            `}
            style={{ 
              width: `${columnWidths[column.field] || 50}px`,
              left: isPinned ? `${getPinnedOffset(column.field)}px` : undefined
            }}
          >
            {total !== undefined ? (
              <span>
                {column.field === 'margin_percentage' ? (
                  `${total.toFixed(2)}%`
                ) : (
                  column.field === 'max_price' || column.field === 'price_guide_value' ? (
                    new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: userCurrency,
                      minimumFractionDigits: 0,
                      maximumFractionDigits: userCurrency === 'JPY' ? 0 : 2,
                      minimumSignificantDigits: 1
                    }).format(userCurrency === 'JPY' ? Math.round(total) : total)
                  ) : (
                    new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: houseCurrency,
                      minimumFractionDigits: houseCurrency === 'JPY' ? 0 : (total % 1 !== 0 ? 2 : 0),
                      maximumFractionDigits: houseCurrency === 'JPY' ? 0 : (total % 1 !== 0 ? 2 : 0)
                    }).format(houseCurrency === 'JPY' ? Math.round(total) : total)
                  )
                )}
              </span>
            ) : ''}
          </td>
        );
      })}
    </>
  );
};

export default React.memo(TableFooter);
